import { FC, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";

import { Box, Button, Grid } from "@mui/material";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";

import ProcedureInfoIcon from "@/assets/svg/icons/prosedyre-info.svg?react";
import UploadFileIcon from "@/assets/svg/interactions/icon-upload-file.svg?react";

import { AdminProtected } from "@/components/Application/AdminProtected";
import { AiButton } from "@/components/Application/AiPrototype";
import { SATOSContentRender } from "@/components/Application/SATOSContentRender";
import { SATOSLink } from "@/components/Application/SATOSLink";
import { Translate } from "@/components/Application/Translate";
import { WrapperLeft } from "@/components/Application/WrapperLeft";
import { WrapperRight } from "@/components/Application/WrapperRight";
import { ChecklistComparisonDialogue } from "@/components/Procedures/ChecklistComparisonDialogue";
import { StyledButton } from "@/components/Shift/ShiftLog";

import { ProcedureDetailNode } from "@/model/Procedure/ProcedureDetailNode";
import { ProcedureNodeStep } from "@/model/Procedure/ProcedureNodeStep";

import { useFetchApplicationFeatures } from "@/service/ApplicationQueries";
import { useClassicMode } from "@/service/Hooks";
import { UpdateProcedureVersion } from "@/service/ProcedureMutations";
import {
	useFetchProcedureNode,
	useFetchReviewChecklist,
	useGetUpdatedProcedures,
} from "@/service/ProcedureQueries";
import {
	useFetchActiveChecklist,
	useFetchActiveProcedureDetails,
	useHandleProcedureDetails,
} from "@/service/ProcedureQueries";
import { RouteParams } from "@/service/Utilities";

interface IAccordionSingle {
	openDetailNodes: Set<number>;
	ToggleOpenDetailNodes: (
		node: ProcedureDetailNode,
		forceOpen: boolean,
	) => void;
}

interface IAccordion extends IAccordionSingle {
	editMode: boolean | undefined;
	readonly: boolean;
	showSingleSchemeId: number;
	procedureId: number;
	newScheme: boolean;
}

export const AccordionSingle: FC<IAccordion> = (props) => {
	const { isLoading, data: procedureDetails } = useFetchProcedureNode(
		props.procedureId,
		props.showSingleSchemeId,
		props.newScheme,
	);

	if (isLoading) {
		return (
			<section className="accordion-wrapper">
				<ul className="accordion-menu"></ul>
			</section>
		);
	}

	if (!procedureDetails) {
		return (
			<section className="accordion-wrapper">
				<ul className="accordion-menu"></ul>
			</section>
		);
	}

	return (
		<section className="accordion-wrapper">
			<ul className="accordion-menu">
				{procedureDetails.ProcedureDetailNodes.map((node, index) => (
					<ProcedureAccordionMenuItem
						key={index}
						editMode={props.editMode}
						item={node}
						openDetailNodes={props.openDetailNodes}
						ToggleOpenDetailNodes={props.ToggleOpenDetailNodes}
						readonly={props.readonly}
						showSingleSchemeId={props.showSingleSchemeId}
					/>
				))}
			</ul>
		</section>
	);
};

interface IAccordionItem extends IAccordionSingle {
	item: ProcedureDetailNode;
	readonly: boolean;
	openDetailNodes: Set<number>;
}

interface IAccordionMainItem extends IAccordionItem {
	editMode: boolean | undefined;
	readonly: boolean;
	showSingleSchemeId: number;
	ToggleOpenDetailNodes: (
		node: ProcedureDetailNode,
		forceOpen: boolean,
	) => void;
}

const ProcedureAccordionMenuItem: FC<IAccordionMainItem> = (props) => {
	const { id } = useParams<RouteParams>();

	if (
		!props.item ||
		(props.showSingleSchemeId > 0 &&
			props.item.SchemeId !== props.showSingleSchemeId)
	) {
		return null;
	}

	const hasChildren = props.item.Children?.length > 0;
	const hasChildrenClassName = hasChildren ? "accordion-item-has-children" : "";
	const isActive = props.openDetailNodes.has(props.item.TreeNodeNumber);
	const isActiveClassName = isActive ? "accordion-item--active" : "";

	return (
		<li
			className={`accordion-item ${hasChildrenClassName} ${isActiveClassName}`}
		>
			<div className="accordion-item-link">
				<h2 className="accordion-item-heading">{props.item.Name}</h2>
				{props.item.DocumentAttached && (
					<SATOSLink
						to="ShowHTMLWord"
						params={[id, props.item.SchemeId]}
						target="_blank"
						className="button-icon"
					>
						<UploadFileIcon className="svg-icon svg-icon--upload-file" />
					</SATOSLink>
				)}
				<div className="accordion-item-step">
					<span>
						{props.item.Done} / {props.item.Total}
					</span>
				</div>
			</div>
			{hasChildren ? (
				<ul className="accordion-menu-sub">
					{props.item.Children.map((child, index) => (
						<ProcedureAccordionSubMenuItem
							key={index}
							item={child}
							openDetailNodes={props.openDetailNodes}
							ToggleOpenDetailNodes={props.ToggleOpenDetailNodes}
							readonly={props.readonly}
						/>
					))}
				</ul>
			) : null}
		</li>
	);
};

const ProcedureAccordionSubMenuItem: FC<IAccordionItem> = (props) => {
	const { id } = useParams<RouteParams>();
	const { groupId } = useParams<RouteParams>();

	if (!props.item) {
		return null;
	}

	return (
		<li className="accordion-item">
			<SATOSLink
				to="viewuppdatedprocedurepage"
				params={[id, groupId, props.item.SchemeId, props.item.CheckListId]}
				className="accordion-item-link"
			>
				<div className="accordion-item-icon">
					<ProcedureInfoIcon className="svg-icon" width="10" />
				</div>
				<h2 className="accordion-item-heading">{props.item.Name}</h2>
				<CheckListStatus
					schemeId={props.item.SchemeId}
					isDeleted={props.item.Deleted}
					checklistId={props.item.CheckListId}
				/>
				<div className="accordion-item-step">
					<span>
						{props.item.Done} / {props.item.Total}
					</span>
				</div>
			</SATOSLink>
		</li>
	);
};

interface ICheckListStatus {
	isDeleted: boolean;
	checklistId: number;
	schemeId: number;
}

export const CheckListStatus: FC<ICheckListStatus> = (props) => {
	if (props.isDeleted) {
		return <div className="status--pending">D</div>;
	}
	if (props.checklistId > 0 && props.schemeId > 0) {
		return <div className="status--active">U</div>;
	}
	if (props.checklistId > 0 && props.schemeId === 0) {
		return <div className="status--active">U</div>;
	}
	return <div className="status--disabled">&mdash;</div>;
};

export const Checklist: FC = (props) => {
	return <>{props.children}</>;
};

interface IProceduresSingleChecklistBody extends IActiveProcedureChecklist {
	selectChecklistType: string;
	checklistId: number | undefined;
	parentSchemeId: number | undefined;
}

interface IProceduresSingleChecklist extends IActiveProcedureChecklist {
	selectChecklistType: string;
}

interface IActiveProcedureChecklist {
	activeSchemeId: number | undefined;
}

export const ChecklistBody: FC<IProceduresSingleChecklistBody> = (props) => {
	const { isLoading, data: activeNodeSteps } = useFetchReviewChecklist(
		props.activeSchemeId,
		props.checklistId,
		props.selectChecklistType === "getupdatedchecklist",
	);
	const classicMode = useClassicMode(props.activeSchemeId);

	if (isLoading) {
		return (
			<section className="procedure-wrapper">
				<div className="procedure-wrapper-inner">
					<div className="grid-wrapper"></div>
				</div>
			</section>
		);
	}

	if (!activeNodeSteps) {
		return (
			<section className="procedure-wrapper">
				<div className="procedure-wrapper-inner">
					<div className="grid-wrapper"></div>
				</div>
			</section>
		);
	}

	if (
		props.activeSchemeId &&
		props.selectChecklistType === "getupdatedchecklist" &&
		!isLoading &&
		activeNodeSteps.Steps.length === 0 &&
		activeNodeSteps.Name === ""
	) {
		return (
			<section className="procedure-wrapper">
				<div className="procedure-wrapper-inner">
					<div className="grid-wrapper">
						<Translate
							String="Procedure.Checklist.NoChanges"
							HTMLTag="h2"
							className="text-center"
						>
							No changes to Checklist
						</Translate>
					</div>
				</div>
			</section>
		);
	}

	if (classicMode) {
		return (
			<section className="procedure-wrapper">
				<div className="procedure-wrapper-inner">
					<div className="grid-wrapper">
						<table className="classic-mode">
							<tbody>
								<ManualHeading activeSchemeId={props.activeSchemeId} />
								{activeNodeSteps.Steps.map((step, index) => (
									<ChecklistRow
										key={index}
										activeSchemeId={props.activeSchemeId}
										step={step}
										showingUpdated={
											props.selectChecklistType === "getupdatedchecklist"
										}
									/>
								))}
							</tbody>
						</table>
					</div>
				</div>
			</section>
		);
	}

	return (
		<section className="procedure-wrapper">
			<div className="procedure-wrapper-inner">
				<div className="grid-wrapper">
					<ManualHeading activeSchemeId={props.activeSchemeId} />
					{activeNodeSteps.Steps.map((step, index) => (
						<ChecklistRow
							key={index}
							activeSchemeId={props.activeSchemeId}
							step={step}
							showingUpdated={
								props.selectChecklistType === "getupdatedchecklist"
							}
						/>
					))}
				</div>
			</div>
		</section>
	);
};

const ManualHeading: FC<IActiveProcedureChecklist> = (props) => {
	const { data: procedureDetails } = useFetchActiveProcedureDetails();

	const { activeChildNode } = useHandleProcedureDetails(
		procedureDetails?.ProcedureDetailNodes,
		props.activeSchemeId,
	);

	if (!activeChildNode?.ImportedChecklist) {
		return (
			<div className="grid-row grid-row-heading">
				<Translate
					String="Procedure.Checklist.ManualHeading"
					HTMLTag="h2"
					className="text-center"
				>
					Activity
				</Translate>
			</div>
		);
	}

	return null;
};

const isHeader = (step: ProcedureNodeStep) => {
	return step.Index === 1 && isMultipleFieldStep(step.NumberOfFields);
};

const isMultipleFieldStep = (numberOfFields: number) => {
	return numberOfFields > 0;
};

const Header: FC<IChecklistStep> = (props) => {
	const classicMode = useClassicMode(props.activeSchemeId);

	if (classicMode) {
		return (
			<SATOSContentRender
				content={props.step.Value}
				rehypePlugins={[rehypeRaw, rehypeSanitize]}
			/>
		);
	}

	return <SATOSContentRender content={props.step.Value} />;
};

interface IChecklistStep extends IActiveProcedureChecklist {
	step: ProcedureNodeStep;
	showingUpdated: boolean;
}

export const ChecklistRow: FC<IChecklistStep> = (props) => {
	const classicMode = useClassicMode(props.activeSchemeId);

	if (isHeader(props.step)) {
		if (classicMode) {
			return (
				<tr className="header-row">
					<Header
						activeSchemeId={props.activeSchemeId}
						step={props.step}
						showingUpdated={false}
					/>
					<td className="header-actions"></td>
				</tr>
			);
		}

		return (
			<div className="grid-row grid-row-header">
				<div className="grid-column grid-column--header">
					<Header
						activeSchemeId={props.activeSchemeId}
						step={props.step}
						showingUpdated={props.showingUpdated}
					/>
				</div>
				<div className="grid-column grid-column-blank"></div>
			</div>
		);
	}
	return (
		<Step
			activeSchemeId={props.activeSchemeId}
			step={props.step}
			showingUpdated={props.showingUpdated}
		/>
	);
};

const Step: FC<IChecklistStep> = (props) => {
	const classicMode = useClassicMode(props.activeSchemeId);

	if (classicMode) {
		return (
			<>
				<tr title={`${props.step.Id}`}>
					<StepClassicModeContent {...props} />
					<td colSpan={1} className="status-actions"></td>
					<td>
						<ChecklistStatus
							showingUpdated={props.showingUpdated}
							isUpdated={props.step.IsModified}
							toDelete={props.step.DeleteThis}
						/>
					</td>
				</tr>
			</>
		);
	}

	return (
		<>
			<div className={getRowTypeClass(props.step)}>
				<div className={getColumnTypeClass(props.step)}>
					<StepContent {...props} />
					<div className="grid-column grid-column--tools">
						<div className="status-actions"></div>
					</div>
				</div>
			</div>
		</>
	);
};

const StepClassicModeContent: FC<IChecklistStep> = (props) => {
	return (
		<SATOSContentRender
			rehypePlugins={[rehypeRaw]}
			content={props.step.Value}
		/>
	);
};

const StepContent: FC<IChecklistStep> = (props) => {
	const { data: procedureDetails } = useFetchActiveProcedureDetails();

	const { activeChildNode } = useHandleProcedureDetails(
		procedureDetails?.ProcedureDetailNodes,
		props.activeSchemeId,
	);

	if (activeChildNode?.ImportedChecklist) {
		return <SATOSContentRender content={props.step.Value} />;
	}

	return (
		<div>
			<SATOSContentRender
				content={props.step.Value}
				rehypePlugins={[rehypeRaw, rehypeSanitize]}
			/>
		</div>
	);
};

interface IChecklistStatus {
	showingUpdated: boolean;
	toDelete: boolean;
	isUpdated: boolean;
}

const ChecklistStatus: FC<IChecklistStatus> = (props) => {
	if (!props.showingUpdated) return null;

	if (props.isUpdated) {
		return (
			<div className="status--active w-5 h-5 mx-auto text-center font-bold">
				U
			</div>
		);
	}

	if (props.toDelete) {
		return (
			<div className="status--pending w-5 h-5 mx-auto text-center font-bold">
				D
			</div>
		);
	}

	return (
		<div className="status--disabled w-5 h-5 mx-auto text-center font-bold">
			&mdash;
		</div>
	);
};

const getRowTypeClass = (step: ProcedureNodeStep) => {
	return step?.ChecklistType === "Info"
		? "grid-row grid-row-heading grid-row-heading-info"
		: "grid-row grid-row-item";
};

const getColumnTypeClass = (step: ProcedureNodeStep) => {
	return step?.ChecklistType === "Info"
		? "grid-column grid-column--info"
		: step.NumberOfFields === 0
			? "grid-column grid-column--chapter"
			: "grid-column grid-column--imported";
};

export const ChecklistHeader: FC<IProceduresSingleChecklist> = (props) => {
	const { data: checklist } = useFetchActiveChecklist(
		props.activeSchemeId,
		0,
		props.selectChecklistType,
	);

	return (
		<div className="heading-wrapper">
			{props.children}
			<div className="heading-wrapper-container">
				<div className="heading-wrapper-user-imported heading-wrapper-left">
					<h2 className="heading">{checklist?.Name}</h2>
				</div>
			</div>
			<div className="heading-wrapper-container">
				<div className="heading-wrapper-user-imported">
					<SATOSContentRender content={checklist?.Ingress ?? ""} />
				</div>
			</div>
			<ChecklistStatus
				showingUpdated={props.selectChecklistType === "getupdatedchecklist"}
				isUpdated={!!checklist?.IsModified}
				toDelete={false}
			/>
		</div>
	);
};

export const InfoColors: FC = () => {
	return (
		<>
			<div className="info-colors">
				<div className="info-color">
					<div className="status--active">U</div>
					<span>Step is Updated</span>
				</div>
				<div className="info-color">
					<div className="status--pending">D</div>
					<span>Step is Deleted</span>
				</div>
				<div className="info-color">
					<div className="status--disabled">&mdash;</div>
					<span>Step is Unchanged</span>
				</div>
			</div>
		</>
	);
};

interface IUpdatedProceduresDetails {
	source: string;
}

export const UpdatedProceduresDetails: FC<IUpdatedProceduresDetails> = (
	props,
) => {
	const { id, groupId, schemeId, checklistid } = useParams<RouteParams>();
	const [returnPage, setReturnPage] = useState<string>(props.source);
	const [goBack, setGoBack] = useState<boolean>(false);
	const [companyId, setCompanyId] = useState<number>(1);
	const [openDetailNodes, setOpenDetailNodes] = useState<Set<number>>(
		new Set<number>(),
	);
	const [checklistId, setChecklistId] = useState<number>(0);
	const [parentSchemeId, setParentSchemeId] = useState<number>(0);
	const [
		isChecklistComparisonDialogueOpen,
		setIsChecklistComparisonDialogueOpen,
	] = useState<boolean>(false);

	const { data: updatedProcedureVersion } = useGetUpdatedProcedures(companyId);
	const { data: appFeatures } = useFetchApplicationFeatures();

	useMemo(() => {
		if (updatedProcedureVersion) {
			setParentSchemeId(parseInt(groupId));
			setChecklistId(parseInt(checklistid));
		}
	}, [updatedProcedureVersion, groupId, checklistid, schemeId]);

	const updateNode = () => {
		update(true);
	};

	const rejectNode = () => {
		update(false);
	};

	const updateProcedureVersion = UpdateProcedureVersion();
	const update = async (update: boolean) => {
		await updateProcedureVersion({
			ProcedureId: parseInt(id),
			NodesChemeId: parentSchemeId,
			checklistId: checklistId,
			DoUpdate: update,
		});
		setGoBack(true);
	};

	if (goBack) {
		if (returnPage === "node") {
			return <Navigate to={{ pathname: `/procedure/${id}` }} />;
		} else {
			return <Navigate to={{ pathname: `/updatedprocedures` }} />;
		}
	}

	const toggleOpenDetailNodes = (
		node: ProcedureDetailNode,
		forceOpen: boolean = true,
	) => {
		if (!node) {
			return;
		}

		if (forceOpen) {
			openDetailNodes.add(node.TreeNodeNumber);
			setOpenDetailNodes(new Set<number>(openDetailNodes));
			return;
		}

		if (openDetailNodes.has(node.TreeNodeNumber)) {
			openDetailNodes.delete(node.TreeNodeNumber);
		} else {
			openDetailNodes.add(node.TreeNodeNumber);
		}

		setOpenDetailNodes(new Set<number>(openDetailNodes));
	};

	return (
		<AdminProtected>
			<Grid container spacing={2}>
				<Grid item xs={12} sm={12} md={6}>
					<WrapperLeft className="wrapper--half hide-accordion-steps">
						<div className="heading-wrapper">
							<div className="heading-wrapper-container">
								<div
									className="heading-wrapper-left"
									style={{ paddingTop: "58px" }}
								>
									<Translate
										className="heading"
										HTMLTag="h2"
										String="Procedures.Update.current.Header"
									>
										Current node.
									</Translate>
								</div>
								<div className="heading-wrapper-right">
									<Button
										variant="contained"
										color="info"
										sx={{
											textTransform: "capitalize",
											maxHeight: "44px",
											marginTop: "50px",
										}}
									>
										<SATOSLink
											to={"AccessProcedure"}
											params={[id]}
											target="_blank"
										>
											<Translate
												HTMLTag="h4"
												String="Procedures.Update.View.Procedure"
											>
												View procedure
											</Translate>
										</SATOSLink>
									</Button>
								</div>
							</div>
						</div>
						<InfoColors />
						<AccordionSingle
							editMode={false}
							openDetailNodes={openDetailNodes}
							ToggleOpenDetailNodes={toggleOpenDetailNodes}
							readonly={true}
							showSingleSchemeId={parentSchemeId}
							procedureId={Number(id)}
							newScheme={false}
						/>
						<Checklist>
							<ChecklistHeader
								activeSchemeId={Number(groupId)}
								selectChecklistType={""}
							></ChecklistHeader>
							<br />
							----------------------------------------
							<ChecklistBody
								activeSchemeId={Number(groupId)}
								checklistId={checklistId}
								parentSchemeId={parentSchemeId}
								selectChecklistType={""}
							/>
						</Checklist>
					</WrapperLeft>
				</Grid>
				<Grid item xs={12} sm={12} md={6}>
					<WrapperRight className="wrapper--half p-4 hide-accordion-steps">
						<div className="heading-wrapper">
							<div className="heading-wrapper-container">
								<div className="heading-wrapper-left">
									<Translate
										className="heading"
										HTMLTag="h2"
										String="Procedures.Update.current.Header"
									>
										Updated node.
									</Translate>
								</div>
								<div className="heading-wrapper-right">
									<Button
										sx={{ mr: 2, textTransform: "capitalize" }}
										variant="outlined"
										color="error"
										onClick={rejectNode}
									>
										<Translate
											HTMLTag="h4"
											String="Procedures.Update.Reject.Update"
										>
											Reject update
										</Translate>
									</Button>
									<Button
										sx={{ textTransform: "capitalize" }}
										variant="outlined"
										color="success"
										onClick={updateNode}
									>
										<Translate HTMLTag="h4" String="Procedures.Update.Update">
											Update
										</Translate>
									</Button>
								</div>
							</div>
						</div>
						<InfoColors />
						<AccordionSingle
							editMode={false}
							openDetailNodes={openDetailNodes}
							ToggleOpenDetailNodes={toggleOpenDetailNodes}
							readonly={true}
							showSingleSchemeId={parentSchemeId}
							procedureId={Number(id)}
							newScheme={true}
						/>
						<Checklist>
							<div className="row-two-column">
								<div>
									<ChecklistHeader
										activeSchemeId={Number(groupId)}
										selectChecklistType={"getupdatedchecklist"}
									></ChecklistHeader>
								</div>
								{Number(groupId) ? (
									<div>
										<div
											className="heading-wrapper-right"
											style={{ justifyContent: "flex-end", gap: "30px" }}
										>
											{/* Beta AI feature: on feature toggle */}
											{appFeatures?.Ai && (
												<AiButton
													isDisabled={Number(checklistid) === 0}
													handleOpen={() =>
														setIsChecklistComparisonDialogueOpen(true)
													}
													isMobile={false}
													label="Compare"
												/>
											)}
										</div>
									</div>
								) : null}
							</div>
							----------------------------------------
							<ChecklistBody
								activeSchemeId={Number(groupId)}
								checklistId={checklistId}
								parentSchemeId={parentSchemeId}
								selectChecklistType={"getupdatedchecklist"}
							/>
						</Checklist>
					</WrapperRight>
				</Grid>
			</Grid>
			{/* Beta AI feature: on feature toggle */}
			<ChecklistComparisonDialogue
				handleClose={setIsChecklistComparisonDialogueOpen}
				open={isChecklistComparisonDialogueOpen}
				checklistId={Number(checklistid)}
				itemId={Number(groupId)}
				procedureId={Number(id)}
			/>
		</AdminProtected>
	);
};

export interface LiveButtonStyling {
	button: React.CSSProperties;
}

export const calculateButtonStyle = (
	aggregatedStatus: AggregatedMeasurementStatus | undefined,
): LiveButtonStyling => {
	switch (aggregatedStatus) {
		case "ok":
			return {
				button: {
					backgroundColor: "green",
					color: "white",
					minWidth: "40px",
					maxWidth: "40px",
					height: "40px",
					"&:hover": {
						backgroundColor: "white",
						color: "green",
					},
				},
			};
		case "alert":
			return {
				button: {
					backgroundColor: "red",
					color: "white",
					minWidth: "40px",
					maxWidth: "40px",
					height: "40px",
					"&:hover": {
						backgroundColor: "white",
						color: "red",
					},
				},
			};
		case "wait":
			return {
				button: {
					backgroundColor: "yellow",
					color: "grey",
					minWidth: "40px",
					maxWidth: "40px",
					height: "40px",
					animation: "pulseButton 2s linear infinite",
					"@keyframes pulseButton": {
						"0%": {
							backgroundColor: "grey",
							color: "white",
						},
						"50%": {
							backgroundColor: "yellow",
						},
						"100%": {
							backgroundColor: "grey",
							color: "white",
						},
					},
				},
			};
		case "skip":
			return {
				button: {
					backgroundColor: "grey",
					color: "white",
					minWidth: "40px",
					maxWidth: "40px",
					height: "40px",
				},
			};
		default:
			return {
				button: {
					backgroundColor: "grey",
					color: "white",
					minWidth: "40px",
					maxWidth: "40px",
					height: "40px",
				},
			};
	}
};

export interface LiveTagStyling {
	container: React.CSSProperties;
	pulseAnimation?: React.CSSProperties;
}

export const calculateStatusListenerTagConfig = (
	aggregatedStatus: AggregatedMeasurementStatus | undefined,
): LiveTagStyling => {
	switch (aggregatedStatus) {
		case "alert":
			return {
				container: {
					background: "#ff969a",
				},
				pulseAnimation: {},
			};
		case "ok":
			return {
				container: {
					background: "#aef6a6",
				},
				pulseAnimation: {},
			};
		case "wait":
			return {
				container: {
					background: "#f9e488",
				},
				pulseAnimation: {
					content: '""',
					position: "absolute",
					left: "0",
					height: "101%",
					width: "101%",
					transform: "translateX(-100%)",
					background:
						"linear-gradient(to right, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 0))",
					animation: "pulseAnimation 1.5s linear infinite",
					"@keyframes pulseAnimation": {
						"0%": {
							transform: "translateX(-100%)",
						},
						"100%": {
							transform: "translateX(100%)",
						},
					},
				},
			};
		case "skip":
			return {
				container: {
					background: "grey",
				},
			};
		default:
			return {
				container: {
					background: "grey",
				},
			};
	}
};
